import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, onMounted, defineAsyncComponent } from 'vue'
import axios from 'axios'
import { Auth } from 'aws-amplify'
import { API_LOCATION } from '@/api-config'

type PinStatus = '' | 'resolved' | 'unresolved' | 'no_address' | 'lookup_failed'
type Pin = { latitude: number; longitude: number }


export default /*#__PURE__*/_defineComponent({
  __name: 'VenuePinField',
  props: {
  companyId: { type: String, required: true },
  groupId: { type: [String, Number], required: true },
  canEdit: { type: Boolean, default: false },
  hasAddress: { type: Boolean, default: false },
},
  setup(__props, { expose }) {

const props = __props

// Venue pin for a group (OTC-2926). GET and PUT companies/{id}/groups/{grid}/location
// (get-group-location, update-group-location). The GET fills the pin from the
// group's address when the address changed, so the pin shows without a save.
// Moving the pin or resetting it only changes pending state; Group settings
// calls savePending() after it saves the address (Update location details).
// Picking a suggested address calls previewAddressPin(): the pin moves on the
// map only; the address save sets the real pin.
const LocationMap = defineAsyncComponent(() => import(/* webpackChunkName: "maplibre" */ '@/components/LocationMap.vue'))



const latitude = ref<number | null>(null)
const longitude = ref<number | null>(null)
const locationSource = ref<string | null>(null)
const status = ref<PinStatus>('')
const addressChanged = ref(false)

const pendingPin = ref<Pin | null>(null)
const pendingReset = ref(false)
const previewPin = ref<Pin | null>(null)
const locateError = ref('')

const url = computed(() => `${API_LOCATION}companies/${props.companyId}/groups/${props.groupId}/location`)
const shownPin = computed(() => pendingPin.value || previewPin.value)
const shownLat = computed(() => (shownPin.value ? shownPin.value.latitude : latitude.value))
const shownLng = computed(() => (shownPin.value ? shownPin.value.longitude : longitude.value))
// A picked address is on the map; its Undo would leave the preview showing
// a pin that won't save, so the reset line stays hidden.
const previewing = computed(() => previewPin.value !== null && pendingPin.value === null)
const movedByHand = computed(() => !pendingReset.value && (pendingPin.value !== null || locationSource.value === 'manual'))
const showMap = computed(() =>
  pendingPin.value !== null ||
  previewPin.value !== null ||
  (status.value === 'resolved' && latitude.value !== null && longitude.value !== null) ||
  status.value === 'unresolved')

async function authConfig() {
  const token = (await Auth.currentSession()).getIdToken().getJwtToken()
  return { headers: { Authorization: token } }
}

async function reload() {
  previewPin.value = null
  try {
    const response = await axios.get(url.value, await authConfig())
    const data = response.data || {}
    latitude.value = data.latitude === null || data.latitude === undefined ? null : Number(data.latitude)
    longitude.value = data.longitude === null || data.longitude === undefined ? null : Number(data.longitude)
    locationSource.value = data.location_source || null
    status.value = data.status || 'lookup_failed'
    addressChanged.value = data.address_changed === true
  } catch (err) {
    status.value = 'lookup_failed'
  }
}

function onMove(pin: Pin) {
  pendingPin.value = pin
  previewPin.value = null
  pendingReset.value = false
  locateError.value = ''
}

function markReset() {
  pendingReset.value = true
  pendingPin.value = null
}

// A picked address moves the pin preview. A pin moved by hand is marked to
// reset, so after the save it follows the new address.
function previewAddressPin(lat: number | null, lng: number | null) {
  if (lat === null || lng === null) return
  previewPin.value = { latitude: lat, longitude: lng }
  pendingPin.value = null
  pendingReset.value = locationSource.value === 'manual'
  locateError.value = ''
}

function hasPendingChange(): boolean {
  return pendingReset.value || pendingPin.value !== null
}

// Saves the pending pin change. Leaves it pending and rethrows on failure.
async function savePending() {
  if (pendingReset.value) {
    await axios.put(url.value, { reset_to_address: true }, await authConfig())
  } else if (pendingPin.value) {
    await axios.put(url.value, { latitude: pendingPin.value.latitude, longitude: pendingPin.value.longitude }, await authConfig())
  }
  pendingReset.value = false
  pendingPin.value = null
  previewPin.value = null
}

onMounted(reload)

expose({ reload, hasPendingChange, savePending, previewAddressPin })

return { __sfc: true,LocationMap, props, latitude, longitude, locationSource, status, addressChanged, pendingPin, pendingReset, previewPin, locateError, url, shownPin, shownLat, shownLng, previewing, movedByHand, showMap, authConfig, reload, onMove, markReset, previewAddressPin, hasPendingChange, savePending }
}

})